import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, FileText, Loader2, Paperclip, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadPerformaInvoicePdf,
  downloadPerformaPaymentReceipt,
  getPerformaInvoiceHistory,
  updatePerformaInvoice,
  uploadFileToS3
} from '../services/api/implementation/impl';
import Button from '../../shared/components/ui/Button';
import { Field } from '../../shared/components/ui/primitives';
import { dayKey, money, shortDate } from './SiteWallet/siteWalletUi';

// Pieces of the Proforma Invoice modal: recording a payment with its bank
// details, the payments made so far with their receipts, the PI's documents,
// and the PDF downloads.

export const PI_PAYMENT_MODES = [
  { value: 'NEFT', label: 'NEFT' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'IMPS', label: 'IMPS' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' }
];

const MODE_LABEL = Object.fromEntries(PI_PAYMENT_MODES.map((m) => [m.value, m.label]));

const input =
  'w-full rounded-control border border-hairline-strong bg-surface-muted px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-brand-700';

const errorText = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const isImage = (url) => /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url);

/** Download button that shows it's working while the server renders the PDF. */
export const PdfButton = ({ label, onDownload, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      await onDownload();
    } catch (err) {
      toast.error(errorText(err, 'Could not download the PDF'));
    } finally {
      setBusy(false);
    }
  };
  if (compact) {
    return (
      <button
        type="button"
        onClick={run}
        disabled={busy}
        title={label}
        className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline disabled:opacity-50"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        {label}
      </button>
    );
  }
  return (
    <Button type="button" displayType="secondary" onClick={run} isLoading={busy}>
      <span className="inline-flex items-center gap-1.5">
        <Download size={14} /> {label}
      </span>
    </Button>
  );
};

export const DownloadPiButton = ({ pi }) => (
  <PdfButton label="Download PI" onDownload={() => downloadPerformaInvoicePdf(pi.id)} />
);

/** Proof photos open full size; anything else is a file link. */
export const FileLinks = ({ urls = [], label = 'File' }) => (
  <div className="flex flex-wrap gap-2">
    {urls.map((url, i) =>
      isImage(url) ? (
        <a key={url + i} href={url} target="_blank" rel="noreferrer" title={`Open ${label}`}>
          <img
            src={url}
            alt={`${label} ${i + 1}`}
            className="h-10 w-10 rounded border border-gray-200 object-cover hover:opacity-80"
          />
        </a>
      ) : (
        <a
          key={url + i}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline"
        >
          <FileText size={12} /> {label} {i + 1}
        </a>
      )
    )}
  </div>
);

const DOC_TYPE = { bill: 'Bill', quotation: 'Quotation', other: 'Document' };

/** Bills and quotes on the PI and its PO. */
export const PiDocuments = ({ documents = [] }) =>
  documents.length ? (
    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
      {documents.map((doc) => (
        <li key={doc.id ?? doc.url} className="flex items-center justify-between gap-3 px-3 py-2">
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 items-center gap-1.5 text-brand-700 hover:underline"
          >
            <FileText size={14} className="shrink-0" />
            <span className="truncate">{doc.name || 'Document'}</span>
          </a>
          <span className="shrink-0 text-xs text-gray-500">
            {DOC_TYPE[doc.type] ?? 'Document'}
            {doc.uploaded_at ? ` · ${shortDate(doc.uploaded_at)}` : ''}
          </span>
        </li>
      ))}
    </ul>
  ) : (
    <p className="text-sm text-gray-500">No documents attached.</p>
  );

/** Every payment recorded against one PI, each with its receipt. */
export const PaymentsMade = ({ piId }) => {
  const payments = useQuery({
    queryKey: ['performaInvoiceHistory', 'pi', piId],
    queryFn: () => getPerformaInvoiceHistory(1, 100, { pi_id: piId }),
    enabled: !!piId
  });
  const rows = payments.data?.data ?? [];

  if (payments.isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!rows.length) return <p className="text-sm text-gray-500">No payments yet.</p>;
  return (
    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-col gap-1 px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-700">
              {shortDate(r.payment_date ?? r.created_at)}
              {r.mode && <span className="text-gray-500"> · {MODE_LABEL[r.mode] ?? r.mode}</span>}
              {r.reference_no && <span className="text-gray-500"> · {r.reference_no}</span>}
            </span>
            <span className="flex items-center gap-4">
              <span className="font-medium tabular-nums">{money(r.paidAmount)}</span>
              <PdfButton
                compact
                label="Receipt"
                onDownload={() => downloadPerformaPaymentReceipt(r.id)}
              />
            </span>
          </div>
          {(r.recorded_by || r.bank_account || r.remark) && (
            <span className="text-xs text-gray-500">
              {[r.recorded_by && `by ${r.recorded_by}`, r.bank_account, r.remark]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
          {r.proof_urls?.length > 0 && <FileLinks urls={r.proof_urls} label="Proof" />}
        </li>
      ))}
    </ul>
  );
};

const EMPTY_FORM = {
  amount: '',
  payment_date: '',
  mode: 'NEFT',
  reference_no: '',
  bank_account: '',
  remark: ''
};

/**
 * Record a payment: the amount, and what the bank statement will show for it.
 * `onDirty` tells the modal whether closing would lose something typed.
 */
export const RecordPaymentForm = ({ pi, onBack, onDone, onDirty }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM, payment_date: dayKey(0) });
  const [proofs, setProofs] = useState([]);
  const [uploading, setUploading] = useState(0);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    onDirty?.(Boolean(form.amount || form.reference_no || form.remark || proofs.length));
  }, [form, proofs, onDirty]);

  const due = Number(pi?.due_amount ?? 0);
  const amount = Number(form.amount);
  const cash = form.mode === 'CASH';

  const uploadProofs = async (files) => {
    for (const file of Array.from(files || []).slice(0, 10 - proofs.length)) {
      setUploading((n) => n + 1);
      try {
        const response = await uploadFileToS3(file);
        const url = response?.data?.files?.[0]?.presignedUrl;
        if (!url) throw new Error('The upload returned no file URL');
        setProofs((p) => [...p, { name: file.name, url }]);
      } catch {
        toast.error(`Could not upload ${file.name}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const save = useMutation({
    mutationFn: () => {
      if (!form.amount || !Number.isFinite(amount) || amount <= 0) {
        throw Object.assign(new Error('Enter an amount greater than 0.'), { isValidation: true });
      }
      if (amount > due) {
        throw Object.assign(new Error(`Only ${money(due)} is due.`), { isValidation: true });
      }
      if (!cash && !form.reference_no.trim()) {
        throw Object.assign(
          new Error(
            form.mode === 'CHEQUE' ? 'Enter the cheque number.' : 'Enter the UTR or transaction ID.'
          ),
          { isValidation: true }
        );
      }
      return updatePerformaInvoice(pi.id, {
        paidAmount: amount,
        payment_date: form.payment_date,
        mode: form.mode,
        reference_no: form.reference_no.trim() || null,
        bank_account: form.bank_account.trim() || null,
        remark: form.remark.trim() || null,
        proof_urls: proofs.map((p) => p.url)
      });
    },
    onSuccess: () => {
      toast.success('Payment recorded');
      onDone?.();
    },
    onError: (err) => toast.error(errorText(err, 'Could not record the payment'))
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-4"
    >
      {!pi.po_id && (
        <p className="text-sm font-semibold italic text-gray-700">
          The purchase order is raised once the advance is paid.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Amount paid" required>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={set('amount')}
            className={input}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, amount: String(due) }))}
            className="mt-1 text-xs font-semibold text-brand-700 hover:underline"
          >
            Pay full due ({money(due)})
          </button>
        </Field>
        <Field label="Payment date" required>
          <input
            type="date"
            value={form.payment_date}
            max={dayKey(0)}
            onChange={set('payment_date')}
            className={input}
          />
        </Field>
        <Field label="Mode" required>
          <select value={form.mode} onChange={set('mode')} className={input}>
            {PI_PAYMENT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={form.mode === 'CHEQUE' ? 'Cheque number' : 'UTR / transaction ID'}
          required={!cash}
        >
          <input
            value={form.reference_no}
            onChange={set('reference_no')}
            maxLength={100}
            className={input}
            placeholder={cash ? 'Voucher number (optional)' : ''}
          />
        </Field>
        <Field label="Paid from (bank account)">
          <input
            value={form.bank_account}
            onChange={set('bank_account')}
            maxLength={100}
            className={input}
            placeholder="e.g. HDFC ••4521"
          />
        </Field>
        <Field label="Remark">
          <input value={form.remark} onChange={set('remark')} maxLength={1000} className={input} />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700">Proof</span>
        <div className="flex flex-wrap items-center gap-2">
          {proofs.map((p, i) => (
            <span
              key={p.url}
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <FileText size={12} /> {p.name}
              <button
                type="button"
                onClick={() => setProofs((list) => list.filter((_, j) => j !== i))}
                aria-label={`Remove ${p.name}`}
                className="text-gray-400 hover:text-danger-fg"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {proofs.length < 10 && (
            <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-semibold text-brand-700 hover:underline">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
              {uploading ? 'Uploading…' : 'Attach bank slip or screenshot'}
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  uploadProofs(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      </div>

      {amount > 0 && (
        <p className="text-sm text-gray-600">
          Due after this payment:{' '}
          <span className="font-semibold">{money(Math.max(due - amount, 0))}</span>
        </p>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
        <Button type="button" displayType="secondary" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" isLoading={save.isPending} disabled={uploading > 0}>
          Record payment
        </Button>
      </div>
    </form>
  );
};
